import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, MapPin, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const typeValues = ['monolocale', 'bilocale', 'trilocale', 'quadrilocale', 'villa', 'altro'];

const ValutazioneGratuita = () => {
  const { lang } = useLanguage();
  const f = t('form', lang);
  const [formData, setFormData] = useState({ nome: '', cognome: '', email: '', telefono: '', localita: '', tipologia: '', messaggio: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

  const handleChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
  const handleSelectChange = (value) => { setFormData(prev => ({ ...prev, tipologia: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await axios.post(`${API}/leads`, formData);
      if (response.status === 200) {
        setStatus({ type: 'success', message: f.success });
        setFormData({ nome: '', cognome: '', email: '', telefono: '', localita: '', tipologia: '', messaggio: '' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: f.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white pt-32 pb-16" data-testid="valutazione-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn} className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{f.title}</h1>
          <p className="text-base md:text-lg text-[#0A1312] max-w-3xl mx-auto" style={{fontFamily: "'Satoshi', sans-serif"}}>{f.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <motion.div {...fadeIn} className="lg:col-span-2">
            <div className="bg-[#F7F9F9] p-8 border border-[#E2E8F0] rounded-sm">
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="nome" className="text-[#0A1312] mb-2 block">{f.nome} *</Label>
                    <Input id="nome" name="nome" data-testid="input-nome" value={formData.nome} onChange={handleChange} required className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="cognome" className="text-[#0A1312] mb-2 block">{f.cognome} *</Label>
                    <Input id="cognome" name="cognome" data-testid="input-cognome" value={formData.cognome} onChange={handleChange} required className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="email" className="text-[#0A1312] mb-2 block">{f.email} *</Label>
                    <Input id="email" name="email" type="email" data-testid="input-email" value={formData.email} onChange={handleChange} required className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="telefono" className="text-[#0A1312] mb-2 block">{f.telefono} *</Label>
                    <Input id="telefono" name="telefono" type="tel" data-testid="input-telefono" value={formData.telefono} onChange={handleChange} required placeholder="+41..." className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="localita" className="text-[#0A1312] mb-2 block">{f.localita} *</Label>
                    <Input id="localita" name="localita" data-testid="input-localita" value={formData.localita} onChange={handleChange} required placeholder="es. Lugano, Locarno..." className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="tipologia" className="text-[#0A1312] mb-2 block">{f.tipologia} *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.tipologia}>
                      <SelectTrigger data-testid="select-tipologia" className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm">
                        <SelectValue placeholder={f.selectPlaceholder} />
                      </SelectTrigger>
                      <SelectContent className="z-[100] bg-white" position="popper" sideOffset={4}>
                        {f.types.map((type, idx) => (
                          <SelectItem key={typeValues[idx]} value={typeValues[idx]} data-testid={`option-${typeValues[idx]}`}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mb-6">
                  <Label htmlFor="messaggio" className="text-[#0A1312] mb-2 block">{f.messaggio}</Label>
                  <Textarea id="messaggio" name="messaggio" data-testid="textarea-messaggio" value={formData.messaggio} onChange={handleChange} rows={4} placeholder={f.messaggioPlaceholder} className="border-[#E2E8F0] focus:ring-1 focus:ring-[#65c3ba] focus:border-[#65c3ba] rounded-sm" />
                </div>
                {status.message && (
                  <div className={`mb-6 p-4 rounded-sm flex items-start ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="form-status-message">
                    {status.type === 'success' ? <CheckCircle className="mr-2 flex-shrink-0" size={20} /> : <AlertCircle className="mr-2 flex-shrink-0" size={20} />}
                    <span>{status.message}</span>
                  </div>
                )}
                <Button type="submit" data-testid="submit-button" disabled={isSubmitting} className="w-full bg-[#65c3ba] text-white hover:bg-[#3e716c] rounded-sm py-6 text-lg font-semibold disabled:opacity-50">
                  {isSubmitting ? f.submitting : f.submit}
                </Button>
              </form>
            </div>
          </motion.div>

          <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
            <div className="space-y-6">
              <div className="bg-[#F7F9F9] p-6 border border-[#E2E8F0] rounded-sm">
                <h3 className="text-xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{f.infoTitle}</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-[#0A1312] mb-1">Homewise Sagl</p>
                    <p className="text-sm text-[#0A1312]">{f.legalSeat}</p>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="text-[#65c3ba] mr-3 mt-1 flex-shrink-0" size={18} />
                    <p className="text-sm text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>Via al Gaggio 15B<br />6912 Pazzallo</p>
                  </div>
                  <div className="flex items-start">
                    <Mail className="text-[#65c3ba] mr-3 mt-1 flex-shrink-0" size={18} />
                    <a href="mailto:info@home-wise.ch" className="text-sm text-[#0A1312] hover:text-[#3e716c] transition-colors">info@home-wise.ch</a>
                  </div>
                  <div className="pt-2 space-y-2">
                    <a href="https://wa.me/41786093451" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-[#0A1312] hover:text-[#3e716c] transition-colors" data-testid="whatsapp-link">
                      <MessageCircle size={16} className="mr-2 text-[#65c3ba]" />
                      {f.whatsapp}
                    </a>
                    <a href="https://www.instagram.com/homewise.ch?igsh=MWxlNXBwaW5zYzJ4NQ==" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-[#0A1312] hover:text-[#3e716c] transition-colors" data-testid="instagram-link">
                      <svg className="mr-2 text-[#65c3ba]" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                      {f.instagram}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#3e716c] text-white p-6 rounded-sm">
                <h3 className="text-lg font-bold mb-3" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{f.whyTitle}</h3>
                <ul className="space-y-2 text-sm" style={{fontFamily: "'Satoshi', sans-serif"}}>
                  {f.whyItems.map((item, idx) => (
                    <li key={`why-${item.slice(0, 20)}`} className="flex items-start">
                      <CheckCircle className="mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ValutazioneGratuita;